import React, { useEffect, useRef } from "react";
import Selecto from "react-selecto";
import useDataGridApi from "@/common/hooks/useDataGridApi";
import { NO_SELECTO_CLASSNAME } from "@/types/types.ts";

type SelectoTracksPropsType = {
  explorerFolderId: string;
}

export const SelectoTracks = (props: SelectoTracksPropsType) => {
  const { explorerFolderId } = props;
  const selectoRef = useRef<Selecto>(null);
  const isScrolling = useRef(false);
  const SELECTO_SELECTION = ".selecto-selection";
  const SCROLL_CONTAINER_SELECTOR = `.MuiDataGrid-virtualScroller`;
  const SELECTABLE_TARGETS = `.playlist-${explorerFolderId}-selectable-row`;
  const scrollElement = document.querySelector(SCROLL_CONTAINER_SELECTOR);
  const [dataGridApi] = useDataGridApi();
  const selectedTrackIds = useRef<Set<string>>(new Set());
  const scrollDirectionRef = useRef(0);

  useEffect(() => {
    if (!scrollElement) {
      return;
    }

    const onScroll = (event: Event) => {
      isScrolling.current = true;

      // Detect scroll direction
      const scrollTop = (event.target as HTMLElement).scrollTop;
      scrollDirectionRef.current = scrollTop > (scrollElement as HTMLElement).scrollTop ? 1 : -1;

      if (!selectoRef || !selectoRef.current) {
        throw Error("No selectoRef ref found");
      }

      selectoRef.current.checkScroll();
    };

    scrollElement.addEventListener("scroll", onScroll);

    return () => {
      scrollElement.removeEventListener("scroll", onScroll);
      isScrolling.current = false;
    };
  }, [scrollElement]);

  const addToSelected = (id: string) => {
    selectedTrackIds.current.add(id);
  };

  const removeFromSelected = (id: string) => {
    selectedTrackIds.current.delete(id);
  };

  const updateSelectionModel = () => {
    if (!dataGridApi?.current?.state) {
      console.log('Warning: dataGridApi is not defined @ SelectoTracks.updateSelectionModel')
      return;
    }

    dataGridApi?.current?.setRowSelectionModel({ids: selectedTrackIds.current, type: 'include'});
  };

  // Functie om te controleren of de muispositie binnen 30px van de rechterkant van het scrollElement is
  const isNearRightEdge = (e: MouseEvent | TouchEvent): boolean => {
    if (!scrollElement) return false;

    const scrollRect = scrollElement.getBoundingClientRect();
    const mouseX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;

    // Berekenen of de muis binnen 50px van de rechterkant is
    const distanceFromRight = scrollRect.right - mouseX;
    return distanceFromRight <= 50;
  };

  return (
    <Selecto
      ref={selectoRef}
      ratio={0}
      hitRate={2}
      selectableTargets={[SELECTABLE_TARGETS]}
      dragCondition={(e) => {
        const inputEvent = e.inputEvent as MouseEvent | TouchEvent;

        // Controleren of de muis binnen 30px van de rechterkant is
        if (isNearRightEdge(inputEvent)) {
          return false; // Selecto uitschakelen als we dicht bij de rechterkant zijn
        }

        if (inputEvent.ctrlKey || inputEvent.metaKey || inputEvent.shiftKey) {
          // @note: this is handled by Mui DataGrid
          return false;
        }

        const waveformContainerId = '#waveform-container';
        const headerClassname = '.MuiDataGrid-columnHeaders';

        return (inputEvent.target as Element).closest(waveformContainerId) === null
          && (inputEvent.target as Element).closest(headerClassname) === null
          && (inputEvent.target as Element).closest(NO_SELECTO_CLASSNAME) === null;
      }}
      onDragEnd={() => {
        selectedTrackIds.current = new Set([]);
      }}
      onDragStart={(e) => {
        const clickedRow = e.inputEvent.target.closest(SELECTABLE_TARGETS) as HTMLDivElement;

        if (clickedRow) {
          const explorerItemId = clickedRow.dataset.id;
          if (explorerItemId) {
            addToSelected(explorerItemId);
            updateSelectionModel();
          }
        }
      }}
      onDrag={(e) => {
        const scrollContainer = document.querySelector(SCROLL_CONTAINER_SELECTOR) as HTMLDivElement;
        const selectableItems = document.querySelectorAll(SELECTABLE_TARGETS) as NodeListOf<HTMLDivElement>;

        if (!scrollContainer) {
          throw Error(`Element met classname ${SCROLL_CONTAINER_SELECTOR} bestaat niet`);
        }

        const selectoSelection = document.querySelector(SELECTO_SELECTION);
        if (!selectoSelection) {
          return;
        }
        const selectoSelectionRect = selectoSelection.getBoundingClientRect();
        const pointerY = e.inputEvent.clientY;

        selectableItems.forEach((row) => {
          const rowRect = row.getBoundingClientRect();
          const trackId = row.dataset.id;

          if (!trackId) {
            return;
          }

          const isOverlapping =
            rowRect.bottom >= selectoSelectionRect.top &&
            rowRect.top <= selectoSelectionRect.bottom;

          const isPointerWithinRow =
            pointerY >= rowRect.top &&
            pointerY <= rowRect.bottom;

          if (isOverlapping || isPointerWithinRow) {
            addToSelected(trackId);
          } else {
            removeFromSelected(trackId);
          }
        });

        updateSelectionModel();
      }}
      onScroll={(e) => {
        const { direction } = e;

        if (!scrollElement) {
          throw Error("scrollElement not defined.");
        }
        scrollElement.scrollBy(direction[0] * 20, direction[1] * 20);
      }}
      scrollOptions={{
        useScroll: true, // Enable scrolling
        checkScrollEvent: true, // Enable scroll event checking
        container: scrollElement as HTMLElement,
        getScrollPosition: ({ container }) => {
          return [
            container.scrollLeft,
            container.scrollTop
          ];
        }
      }}
    />
  );
};
